import { useEffect, useState } from "react";
import { useIsPhone } from "../lib/useIsPhone";
import { tryApiGet } from "../api/client";
import { FIRST_CALCULATION_TEXT } from '../lib/definitions/analysis';
import {
  LOCATION_CARD_TITLE, LOCATION_NO_VALUE, LOCATION_SETTINGS_PATH, LOCATION_TIPS,
  locationHeadline, locationLimitsLine, locationLinkPassed, locationNumber, locationReported, locationResultLabel,
  type LocationCard,
} from "../lib/locationCheck";
import { LocationLightTag, LocationReasonTags } from "./LocationCheckTab";
import { HoverTip } from "./HoverTip";
import { ResponsiveRecordTable } from "./ResponsiveRecordTable";
import { SectionCard } from "./SectionCard";

// The node page's Location Check card: the node's own numbers and its direct links, read from the
// stored Impossible Location snapshot (never computed on request).
export function NodeLocationSection({ nodeId, protocol }: { nodeId: string; protocol: "meshcore" | "meshtastic" }) {
  const [data, setData] = useState<LocationCard | null | "pending" | "unavailable">(null);
  const phone = useIsPhone();
  const [showPassing, setShowPassing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    tryApiGet<LocationCard | { pending: true }>(
      `/api/audit/location-check/node?protocol=${protocol}&window=7d&node=${encodeURIComponent(nodeId)}`,
    )
      .then((res) => {
        if (cancelled) return;
        if (!res) setData("unavailable");
        else if ("pending" in res && res.pending) setData("pending");
        else setData(res as LocationCard);
      })
      .catch(() => !cancelled && setData("unavailable"));
    return () => { cancelled = true; };
  }, [nodeId, protocol]);

  if (data === "unavailable") return null;
  const card = data && data !== "pending" ? data : null;

  const columns = [
    { key: "to", header: "Link To", mobileLabel: "Link To" },
    { key: "km", header: tip("km", LOCATION_TIPS.worstLink), mobileLabel: "km", className: "num" },
    { key: "clear", header: tip("Clearance m", LOCATION_TIPS.clearance), mobileLabel: "Clearance m", className: "num" },
    { key: "result", header: "Result", mobileLabel: "Result" },
  ];
  const allLinks = card?.links ?? [];
  const passing = allLinks.filter((l) => locationLinkPassed(l.result));
  // On a phone failed links come first and passing ones fold away behind a button.
  const shownLinks = phone
    ? [...allLinks.filter((l) => !locationLinkPassed(l.result)), ...(showPassing ? passing : [])]
    : allLinks;
  const rows = shownLinks.map((l) => ({
    key: l.key,
    cells: {
      to: <a href={`#/nodes/${encodeURIComponent(l.key)}`} className="node-name-link">{l.name}</a>,
      km: locationNumber(l.km),
      clear: l.clearance_m === null ? LOCATION_NO_VALUE : locationNumber(l.clearance_m),
      result: <span className="location-result" data-result={l.result}>{locationResultLabel(l.result, l.partner_impossible)}</span>,
    },
  }));

  return (
    <SectionCard title={LOCATION_CARD_TITLE}>
      {data === null && <div className="node-detail-note">Loading…</div>}
      {data === "pending" && <div className="node-detail-note">{FIRST_CALCULATION_TEXT}</div>}
      {card && (
        <>
          <div className="location-card-head">
            <LocationLightTag light={card.light} />
            <span>{locationHeadline(card)}</span>
          </div>
          <dl className="location-card-facts">
            <div><dt>Reported</dt><dd>{locationReported(card.lat, card.lon)}</dd></div>
            <div><dt>Ground Elevation</dt><dd>{card.elevation_m == null ? LOCATION_NO_VALUE : `${locationNumber(card.elevation_m)} m`}</dd></div>
            <div><dt>{tip("Estimate Offset", LOCATION_TIPS.offEstimate)}</dt><dd>{card.estimate_offset_km == null ? LOCATION_NO_VALUE : `${locationNumber(card.estimate_offset_km)} km`}</dd></div>
            <div><dt>Sign Flip</dt><dd>{card.sign_flip ? "Yes" : "No"}</dd></div>
            {(card.reasons ?? []).length > 0 && <div><dt>Reason</dt><dd><LocationReasonTags row={card} /></dd></div>}
          </dl>
          <ResponsiveRecordTable columns={columns} rows={rows} tableClassName="node-list-table location-table" ariaLabel="Location Check links" />
          {phone && passing.length > 0 && (
            <button type="button" className="rf-link-btn location-passing-btn" aria-expanded={showPassing} onClick={() => setShowPassing(!showPassing)}>
              {showPassing ? "Hide Passing Links" : `Show Passing Links (${passing.length})`}
            </button>
          )}
          <div className="location-footnote">
            <span>{locationLimitsLine(card.limits)}</span>
            <a href="#/settings?category=audit&group=location">({LOCATION_SETTINGS_PATH})</a>
          </div>
        </>
      )}
    </SectionCard>
  );
}

function tip(label: string, text: string) {
  return <HoverTip placement="above" text={text}>{label}</HoverTip>;
}
